/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _get from 'lodash/get';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _head from 'lodash/head';
import _reduce from 'lodash/reduce';
import _isEqual from 'lodash/isEqual';
import _filter from 'lodash/filter';
import _isArray from 'lodash/isArray';
import _intersection from 'lodash/intersection';
import { findAttachments } from '../helpers/assets';
import auth from '../providers/auth-service';

import { AssetType, AssetStatus } from '@3di-cloud-substance/models';

export const PREVIEW_TYPES = {
  THUMBNAIL: 'main',
  PRESET: 'preset',
  FAR: 'far',
  CLOSEUP: 'closeup',
  MODEL: 'model',
  BACKPLATE: 'backplate',
  FLAT: 'flat',
};

export const ASSET_TYPE = {
  MATERIAL: AssetType.MATERIAL,
  ATLAS: AssetType.ATLAS,
  DECAL: AssetType.DECAL,
  IBL: AssetType.IBL,
  MODEL: AssetType.MODEL,
  SUBSTANCE_MODEL: AssetType.SUBSTANCE_MODEL,
  SUBSTANCE_TEXTURE_GENERATOR: AssetType.TEXTURE_GENERATOR,
  SUBSTANCE_RIBBON: AssetType.RIBBON,
};

export const ASSET_TYPE_LABEL = {
  [ASSET_TYPE.MATERIAL]: 'Material',
  [ASSET_TYPE.ATLAS]: 'Atlas',
  [ASSET_TYPE.DECAL]: 'Decal',
  [ASSET_TYPE.IBL]: 'IBL',
  [ASSET_TYPE.MODEL]: 'Model',
  [ASSET_TYPE.SUBSTANCE_MODEL]: 'Substance Model',
  [ASSET_TYPE.SUBSTANCE_TEXTURE_GENERATOR]: 'Substance Texture Generator',
  [ASSET_TYPE.SUBSTANCE_RIBBON]: 'Substance Ribbon',
};

export const ASSET_TYPE_PREVIEW_TYPES = {
  [ASSET_TYPE.MATERIAL]: [
    PREVIEW_TYPES.PRESET,
    PREVIEW_TYPES.FAR,
    PREVIEW_TYPES.CLOSEUP,
  ],
  [ASSET_TYPE.ATLAS]: [
    PREVIEW_TYPES.PRESET,
    PREVIEW_TYPES.FAR,
    PREVIEW_TYPES.CLOSEUP,
  ],
  [ASSET_TYPE.DECAL]: [
    PREVIEW_TYPES.PRESET,
    PREVIEW_TYPES.FAR,
    PREVIEW_TYPES.CLOSEUP,
  ],
  [ASSET_TYPE.IBL]: [PREVIEW_TYPES.BACKPLATE, PREVIEW_TYPES.FLAT],
  [ASSET_TYPE.SUBSTANCE_TEXTURE_GENERATOR]: [
    PREVIEW_TYPES.PRESET,
    PREVIEW_TYPES.FAR,
    PREVIEW_TYPES.CLOSEUP,
  ],
  [ASSET_TYPE.SUBSTANCE_RIBBON]: [
    PREVIEW_TYPES.PRESET,
    PREVIEW_TYPES.FAR,
    PREVIEW_TYPES.CLOSEUP,
  ],
};

export const DOWNLOAD_FORMATS = {
  sbsar: 'sbsar',
  sbs: 'sbs',
  mdl: 'mdl',
  fbx: 'fbx',
  glb: 'glb',
  exr: 'exr',
  sbsm: 'sbsm',
  stage: 'stage',
  sppr: 'sppr',
  usd: 'usd',
  usdz: 'usdz',
};

export const FORMATS_FILE_TYPE = {
  sbsar: '.sbsar',
  sbs: '.zip, .sbs',
  mdl: '.mdl',
  fbx: '.fbx',
  glb: '.glb',
  exr: '.exr',
  sbsm: '.sbsm',
  stage: '.zip',
  usd: '.usd',
  usdz: '.usdz',
};

export const DOWNLOAD_FORMATS_LABEL = {
  [DOWNLOAD_FORMATS.sbsar]: 'Substance format',
  [DOWNLOAD_FORMATS.sbs]: 'Substance format (SBS)',
  [DOWNLOAD_FORMATS.sbsm]: 'Substance Model format (SBSM)',
  [DOWNLOAD_FORMATS.mdl]: 'MDL format',
  [DOWNLOAD_FORMATS.fbx]: 'FBX format',
  [DOWNLOAD_FORMATS.glb]: 'GLB format (GLTF Binary)',
  [DOWNLOAD_FORMATS.exr]: 'OpenEXR format (EXR)',
  [DOWNLOAD_FORMATS.stage]: 'Environment Stage (ZIP)',
  [DOWNLOAD_FORMATS.usd]: 'USD format (USD)',
  [DOWNLOAD_FORMATS.usdz]: 'USDZ format (USDZ)',
  [DOWNLOAD_FORMATS.sppr]: 'Substance Ribbon format (SPPR)',
};

export const MAIN_DOWNLOAD_FORMAT = {
  [ASSET_TYPE.MATERIAL]: DOWNLOAD_FORMATS.sbsar,
  [ASSET_TYPE.ATLAS]: DOWNLOAD_FORMATS.sbsar,
  [ASSET_TYPE.DECAL]: DOWNLOAD_FORMATS.sbsar,
  [ASSET_TYPE.IBL]: DOWNLOAD_FORMATS.sbsar,
  [ASSET_TYPE.MODEL]: DOWNLOAD_FORMATS.fbx,
  [ASSET_TYPE.SUBSTANCE_MODEL]: DOWNLOAD_FORMATS.sbsm,
  [ASSET_TYPE.SUBSTANCE_TEXTURE_GENERATOR]: DOWNLOAD_FORMATS.sbsar,
  [ASSET_TYPE.SUBSTANCE_RIBBON]: DOWNLOAD_FORMATS.sppr,
};

export const PREVIEW_ATTACHMENTS_TYPE = {
  [PREVIEW_TYPES.THUMBNAIL]: {
    label: 'Thumbnail',
    tags: ['preview', 'main', 'thumbnail', 'default'],
  },
  [PREVIEW_TYPES.CLOSEUP]: {
    label: 'Closeup default',
    tags: ['preview', 'closeup', 'default'],
  },
  [PREVIEW_TYPES.FAR]: {
    label: 'Far default',
    tags: ['preview', 'far', 'default'],
  },
  [PREVIEW_TYPES.PRESET]: {
    label: 'Preset',
    tags: ['preview', 'preset', 'thumbnail'],
  },
  [PREVIEW_TYPES.BACKPLATE]: {
    label: 'Backplate',
    tags: ['preview', 'backplate'],
  },
  [PREVIEW_TYPES.FLAT]: {
    label: 'Flat',
    tags: ['preview', 'flat'],
  },
  [PREVIEW_TYPES.MODEL]: {
    label: 'Backplate',
    tags: ['model', '3d'],
  },
};

export const ASSET_STATUS = AssetStatus;

export const ASSET_STATUSES = [
  ['pending', ASSET_STATUS.PENDING],
  ['Draft', ASSET_STATUS.DRAFT],
  ['Published', ASSET_STATUS.PUBLISHED],
  ['Archived', ASSET_STATUS.ARCHIVED],
  ['Reported', ASSET_STATUS.REPORTED],
];

function getFormats(attachments) {
  let formats = [];
  for (const attachment of attachments) {
    if (attachment.type === 'download')
      formats.push({ type: attachment.tags[0], url: attachment.url });
  }
  return formats;
}

function getPlayerURL(id, extraData) {
  let view = 'default';
  if (
    extraData &&
    extraData.previewStyleSketchfab &&
    !extraData.previewStyleSketchfab.match(/(sphere)/i)
  )
    view = extraData.previewStyleSketchfab;

  return `https://cloud-player.allegorithmic.com/view/${id}?view=${view}`;
}

export function getDownload(attachments, tag) {
  const download = _find(
    attachments,
    (a) => a.__typename === 'DownloadAttachment' && a.tags.indexOf(tag) !== -1,
  );
  const id = _get(download, 'id', null);

  if (!id) return null;

  const url = _get(download, 'url', null);
  const revisions = _get(download, 'revisions', []);

  return _map(revisions, (r) => {
    const title = _get(r, 'filename', null);
    const revision = _get(r, 'revision', null);
    const size = _get(r, 'size', null);
    return {
      id,
      title,
      url: url ? `${url}?accessToken=${auth.accessToken}&rev=${revision}` : '',
      size: size,
      revision: revision,
    };
  });
}

function mapExtraData(extraData) {
  if (_isArray(extraData))
    return _reduce(
      extraData,
      (items, data) => {
        const key = data.key.replace('.', '_#_');
        items[key] =
          data.key === 'type' && data.value === 'scanned'
            ? 'captured'
            : data.value;
        return items;
      },
      {},
    );
  else return extraData;
}

export function equal(value, other) {
  return (
    value.id === other.id &&
    value.label === other.label &&
    value.url === other.url &&
    _isEqual(value.tags, other.tags)
  );
}

export function getPreviewType(tags) {
  if (!tags) return null;
  const types = Object.values(PREVIEW_TYPES);
  return _head(_intersection(types, tags));
}

export function getPreviews(attachments) {
  if (!attachments) return null;

  return _map(
    findAttachments(attachments, {
      __typename: 'PreviewAttachment',
    }),
    (p) => ({
      id: p.id,
      label: p.label,
      tags: p.tags,
      file: {
        title: p.label,
        url: p.url,
      },
      url: p.url,
      type: getPreviewType(p.tags),
    }),
  );
}

const asset = ({
  id = '',
  title = '',
  description = '',
  createdAt = '',
  updatedAt = '',
  __typename = '',
  tags = [],
  extraData = [],
  status = 'draft',
  cost = 0,
  attachments = [],
  categories = [],
  info = '',
  collections = [],
  lineNumber,
  thumbnail,
}) => ({
  id,
  title,
  description,
  createdAt,
  updatedAt,
  tags,
  status,
  cost: cost.amount || cost, // REST API vs GraphQL response
  attachments,
  categories,
  info,
  collections,
  lineNumber,
  thumbnail,
  __typename,
  get previews() {
    const previews = getPreviews(this.attachments);

    // removing main preview from list of previews
    return _filter(
      previews,
      (p) =>
        p.type !== PREVIEW_TYPES.THUMBNAIL && p.type !== PREVIEW_TYPES.MODEL,
    );
  },

  get modelPreview() {
    const previews = getPreviews(this.attachments);
    return _find(previews, (p) => p.type === PREVIEW_TYPES.MODEL);
  },

  get extraData() {
    return mapExtraData(extraData);
  },

  get formats() {
    return getFormats(this.attachments);
  },

  get playerURL() {
    return getPlayerURL(this.id, this.extraData);
  },

  get sbsar() {
    return getDownload(this.attachments, 'sbsar');
  },

  get sppr() {
    return getDownload(this.attachments, 'sppr');
  },

  get sbs() {
    return getDownload(this.attachments, 'sbs');
  },

  get mdl() {
    return getDownload(this.attachments, 'mdl');
  },

  get fbx() {
    return getDownload(this.attachments, 'fbx');
  },

  get glb() {
    return getDownload(this.attachments, 'glb');
  },

  get usd() {
    return getDownload(this.attachments, 'usd');
  },

  get usdz() {
    return getDownload(this.attachments, 'usdz');
  },

  get exr() {
    return getDownload(this.attachments, 'exr');
  },

  get stage() {
    return getDownload(this.attachments, 'stage');
  },

  get assetTypeLabel() {
    return ASSET_TYPE_LABEL[this.type];
  },

  get type() {
    return this.__typename;
  },
});

export default asset;
